import './Annuaire.css';
import '../../composants/SuspensionModal/SuspensionModal.css'; // FRONT-19: Badge suspendu
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import TabNav from '../../composants/TabNav/TabNav';
import TableauDeBord from '../../composants/TableauDeBord/TableauDeBord';
import { ReactComponent as DocumentsIcon } from "../../assets/icons/document.svg";
import { useAuth } from '../../contexts/AuthContext';
import { isClientRole, isPrestataireRole, isAdminRole } from '../../utils/roleUtils';
import Button from '../../composants/Button/Button';
import DocumentsList from '../../composants/DocumentsList/DocumentsList';
import { FlagCell } from '../../composants/FlagCell/FlagCell';

// Interfaces pour les données de l'API
interface Entite {
  id: number;
  nom: string;
  type: string;
  logo_url?: string | null;
  suspendu?: boolean; // FRONT-19: Statut de suspension
}

interface LotTechnique {
  id: number;
  nom: string;
  code: string;
}

interface Prestataire {
  id: number;
  nom: string;
  prenom: string;
  email: string;
  telephone: string;
  fonction: string;
}

interface Departement {
  id: number;
  nom: string;
}

interface Contact {
  id: number | string;  // FRONT-13: Peut être composite (ex: "123_lot_100")
  rang: number;
  numero: string;
  entite: Entite;
  lot_technique: LotTechnique;
  prestataire: Prestataire;
  departements: Departement[];
  // Autres champs potentiellement ajoutés pour la vue
  commandesCount?: number;
  commandesValue?: number;
  documents?: { 
    id: number;
    type: string;
    category?: 'accord_cadre' | 'entreprise' | 'commun';  // FRONT-13: Catégorie du document
    key?: string;
    commentaire?: string;
    filename?: string;
    updated_at: string;
  }[];
}

interface ApiResponse {
  status: string;
  data: Contact[];
  pagination: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  };
}

// Modification de l'interface FilterOption pour permettre les valeurs numériques
interface FilterOption {
  label: string;
  value: string | number;
}

// Ajout des interfaces pour les nouvelles structures de données API
interface LotTechniqueApi {
  id: number;
  nom: string;
  code: string;
}

interface DepartementApi {
  id: number;
  id_region: number;
  nom: string;
  created_at: string;
  updated_at: string;
}

interface LotDepartementApi {
  id: number;
  id_departement: number;
  created_at: string;
  updated_at: string;
  departement: DepartementApi;
  contrats: any[]; // Structure simplifiée pour ce cas d'usage
}

interface LotDepartementsResponse {
  [key: string]: LotDepartementApi;
}

const Annuaire: React.FC = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [acheteurs, setAcheteurs] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const [modalDocuments, setModalDocuments] = useState<Contact['documents']>([]);
  const [modalTitle, setModalTitle] = useState<string>('');
  // Nouvel état pour stocker les données des API
  const [lotsTechniques, setLotsTechniques] = useState<LotTechniqueApi[]>([]);
  const [lotsDepartements, setLotsDepartements] = useState<LotDepartementApi[]>([]);
  const [openDocumentModalId, setOpenDocumentModalId] = useState<number | string | null>(null);  // Peut être string (ID composite)


  const [paginationContacts, setPaginationContacts] = useState({
    currentPage: 1,
    lastPage: 1,
    perPage: 20,
    total: 0
  });

  const [paginationAcheteurs, setPaginationAcheteurs] = useState({
    currentPage: 1,
    lastPage: 1,
    perPage: 20,
    total: 0
  });

  // États pour les filtres
  const [departementOptions, setDepartementOptions] = useState<FilterOption[]>([]);
  const [lotOptions, setLotOptions] = useState<FilterOption[]>([{ label: "Tous les lots", value: "" }]);
  const [rangOptions, setRangOptions] = useState<FilterOption[]>([{ label: "Tous", value: "" }]);
  const [nomOptions, setNomOptions] = useState<FilterOption[]>([{ label: "Toutes les entreprises", value: "" }]);

  const [selectedDepartement, setSelectedDepartement] = useState<string | number>(75);
  const [selectedLot, setSelectedLot] = useState<string | number>("");
  const [selectedRang, setSelectedRang] = useState<string>("");
  const [selectedNom, setSelectedNom] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState<string>("");

  // État pour stocker tous les contacts (non filtrés)
  const [allContacts, setAllContacts] = useState<Contact[]>([]);

  // Utilisation de roleUtils pour déterminer le rôle de l'utilisateur
  const isClient = isClientRole(user?.role || '');
  const isPrestataire = isPrestataireRole(user?.role || '');
  const isAdmin = isAdminRole(user?.role || '');

  useEffect(() => {
    if (openDocumentModalId !== null) {
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden'; // ajouter html
    } else {
      document.body.style.overflow = 'auto';
      document.documentElement.style.overflow = 'auto';
    }

    return () => {
      document.body.style.overflow = 'auto';
      document.documentElement.style.overflow = 'auto';
    };
  }, [openDocumentModalId]);

  // Définir les onglets en fonction du rôle
  const tabs = isClient
    ? ["Prestataires", "Acheteurs"]
    : ["Acheteurs", "Prestataires"];

  // Fonction pour déterminer si l'onglet actuel est "Annuaire des acheteurs"
  const isAcheteurTab = () => {
    if (isClient && activeTab === 1) return true;
    if (!isClient && activeTab === 0) return true;
    return false;
  };


  const getColumns = () => {
    const baseColumns = [
      {
        field: 'lot_technique',
        header: 'Catégories',
        renderCell: (_: any, row: Contact) => (
          <div
            className="categorie-cell"
            style={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: '180px'
            }}
            title={`${row.lot_technique?.code} - ${row.lot_technique?.nom}` || '-'}
          >
            {`${row.lot_technique?.code} - ${row.lot_technique?.nom}` || '-'}
          </div>
        )
      },
      {
        field: 'departements',
        header: 'Départements',
        renderCell: (_: any, row: Contact) => {
          // Si aucun département n'est sélectionné, afficher tous les départements comme avant
          if (!selectedDepartement) {
            return (
              <div className="departement-cell">
                {row.departements && row.departements.length > 0
                  ? row.departements.map(dep => dep.nom).join(', ')
                  : '-'}
              </div>
            );
          }

          // Sinon, chercher le département sélectionné dans la liste des départements du contact
          const selectedDep = row.departements?.find(dep => dep.id === Number(selectedDepartement));

          return (
            <div className="departement-cell">
              {selectedDep ? `${selectedDep.id} - ${selectedDep.nom}` : '-'}
            </div>
          );
        }
      },
      {
        field: 'rang',
        header: 'Type',
        renderCell: (_: any, row: Contact) => (
          <div className="rang-cell" style={{ whiteSpace: 'nowrap', textAlign: 'left', paddingLeft: '16px', minWidth: '160px' }}>
            {row.rang === 0 ? 'Libre' : `Titulaire rang ${row.rang}`}
          </div>
        )
      },
      {
        field: 'logo',
        header: 'Logo',
        renderCell: (_: any, row: Contact) => {
          // Si pas de logo_url, ne rien afficher
          if (!row.entite?.logo_url) {
            return <div style={{ width: 40, height: 40 }}></div>;
          }

          return (
            <div style={{ width: '100%', display: 'flex', justifyContent: 'flex-end', paddingRight: '8px' }}>
              <img
                src={row.entite.logo_url}
                alt={`${row.entite.nom} logo`}
                className="entite-logo"
                style={{ width: 40, height: 40, objectFit: 'contain' }}
                onError={(e) => {
                  // En cas d'erreur, cacher l'image
                  e.currentTarget.style.display = 'none';
                }}
              />
            </div>
          );
        }
      },
      {
        field: 'entite',
        header: 'Entreprise',
        renderCell: (_: any, row: Contact) => (
          <div
            className="entreprise-cell"
            style={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: '280px',
              minWidth: '200px'
            }}
            title={row.entite?.nom.toUpperCase() || '-'}
          >
            {row.entite?.nom.toUpperCase() || '-'}
          </div>
        )
      },
      ...(!isPrestataire && user ? [{
        field: 'prestataire',
        header: 'Information',
        renderCell: (_: any, row: Contact) => (
          <div className="contact-info-cell" style={{ maxWidth: '200px' }}>
            <div className="contact-name" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {row.prestataire ? `${row.prestataire.prenom} ${row.prestataire.nom}` : '-'}
            </div>
            <div className="contact-email" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {row.prestataire?.email || '-'}
            </div>
            <div className="contact-phone" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {row.prestataire?.telephone || '-'}
            </div>
          </div>
        )
      },
      // FRONT-19: Colonne Statut (badge suspendu)
      {
        field: 'statut',
        header: 'Statut',
        renderCell: (_: any, row: Contact) => (
          row.entite?.suspendu
            ? <span className="annuaire-badge-suspendu">Suspendu</span>
            : null
        )
      },
      {
        field: 'documents',
        header: 'Documents',
        renderCell: (_: any, row: Contact) => {
          if (!user) return <span className="no-documents">Vous devez vous connecter</span>;
          
          // Les documents sont maintenant directement inclus dans row.documents par le backend
          // Les documents communs (CCTP) sont inclus uniquement pour les titulaires (pas les PL)
          const allDocuments = row.documents || [];
          
          // Afficher "Aucun document" uniquement si vraiment aucun document
          if (!allDocuments.length) {
            return <Button className="no-documents documents-button" value="Aucun document" variant='secondary' size='small' disabled />;
          }

          return (
            <Button
              onClick={() => {
                setModalDocuments(allDocuments);
                setModalTitle(`Documents de ${row.entite?.nom || 'ce contact'}`);
                setOpenDocumentModalId(row.id);
              }}
              className="documents-button"
              style={{ padding: '1rem 0.5rem', paddingInline: '0.5rem', paddingBlock: '1rem' }}
              size='small'
              value={`Voir les documents (${allDocuments.length})`}
            />


          );
        }
      }
        // {
        //   field: 'Evaluation',
        //   header: 'Évaluation',
        //   renderCell: (_: any, row: Contact) => (
        //     <div className="contact-info-cell">
        //     </div>
        //   )
        // },
      ] : []),
    ];

    return baseColumns;
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query);
  };

  const handleExport = () => {
    console.log('Exporting...');
  };

  const handleFilter = () => {
    console.log('Filtering...');
  };

  // Fonction pour filtrer les contacts en front-end
  const filterContactsLocally = () => {
    let filtered = [...allContacts];

    console.log("=== DÉBUT FILTRAGE ===");
    console.log("Total contacts:", filtered.length);

    // Filtrer par recherche
    if (searchQuery.trim()) {
      const searchTerms = searchQuery.toLowerCase().split(' ').filter(term => term.length > 0);
      filtered = filtered.filter(contact => {
        const searchableText = `
        ${contact.entite?.nom || ''}
        ${contact.prestataire?.nom || ''}
        ${contact.prestataire?.prenom || ''}
        ${contact.prestataire?.email || ''}
        ${contact.lot_technique?.nom || ''}
        ${contact.lot_technique?.code || ''}
      `.toLowerCase();

        return searchTerms.every(term => searchableText.includes(term));
      });
      console.log("Après recherche:", filtered.length);
    }

    // ❌ PAS de filtre par département ici (fait côté backend maintenant)

    // Filtrer par lot technique (utiliser CODE pas ID)
    if (selectedLot !== "") {
      const lotCode = String(selectedLot);
      console.log("Filtrage par lot CODE:", lotCode);

      filtered = filtered.filter(contact => {
        const contactLotCode = String(contact.lot_technique?.code || '');
        const match = contactLotCode === lotCode;
        return match;
      });

      console.log(`Après filtre lot technique (${lotCode}):`, filtered.length);
    }

    // Filtrer par rang
    if (selectedRang !== "") {
      const rangValue = Number(selectedRang);
      console.log("Filtrage par rang:", rangValue);

      filtered = filtered.filter(contact => {
        const match = contact.rang === rangValue;
        return match;
      });

      console.log(`Après filtre rang (${rangValue}):`, filtered.length);
    }

    // Filtrer par nom d'entreprise
    if (selectedNom.trim()) {
      const nomSearch = selectedNom.toLowerCase().trim();
      filtered = filtered.filter(contact =>
        contact.entite?.nom?.toLowerCase().includes(nomSearch)
      );
      console.log(`Après filtre nom (${nomSearch}):`, filtered.length);
    }

    // Trier les résultats : catégorie → lot → rang (1,2,3 puis référencé) → département
    const sortedFiltered = filtered.sort((a, b) => {
      // 1. Tri par catégorie (centaine du lot_technique.code)
      const lotCodeA = Number(a.lot_technique?.code) || 0;
      const lotCodeB = Number(b.lot_technique?.code) || 0;

      const categoryA = Math.floor(lotCodeA / 100);
      const categoryB = Math.floor(lotCodeB / 100);

      if (categoryA !== categoryB) {
        return categoryA - categoryB;
      }

      // 2. Tri par lot technique (code)
      if (lotCodeA !== lotCodeB) {
        return lotCodeA - lotCodeB;
      }

      // 3. Tri par rang (Type) : rang 1, 2, 3, puis référencé (0)
      const rangA = a.rang ?? 999; // Utiliser 999 pour les valeurs null/undefined
      const rangB = b.rang ?? 999;

      // Les référencés (rang 0) viennent en dernier, avant les valeurs nulles
      if (rangA === 0) {
        if (rangB === 0 || rangB === 999) {
          // Si les deux sont référencés ou null, continuer au tri suivant
          if (rangB === 999) return -1; // 0 avant null
          // Les deux sont 0, continuer
        } else {
          return 1; // 0 après les rangs 1,2,3
        }
      } else if (rangB === 0) {
        return -1; // Les rangs 1,2,3 avant 0
      }

      // Sinon tri croissant des rangs (1 avant 2, 2 avant 3)
      if (rangA !== rangB) {
        return rangA - rangB;
      }

      // 4. Tri par département
      // Si un département est sélectionné, utiliser celui-là pour le tri
      // Sinon, utiliser le premier département de la liste
      let depIdA = 999;
      let depIdB = 999;

      if (selectedDepartement !== "") {
        // Utiliser le département sélectionné s'il existe dans la liste
        const selectedDepId = Number(selectedDepartement);
        depIdA = a.departements?.some(d => d.id === selectedDepId) ? selectedDepId : 999;
        depIdB = b.departements?.some(d => d.id === selectedDepId) ? selectedDepId : 999;
      } else {
        // Sinon utiliser le premier département
        depIdA = a.departements?.[0]?.id || 999;
        depIdB = b.departements?.[0]?.id || 999;
      }

      if (depIdA !== depIdB) {
        return depIdA - depIdB;
      }

      // 5. En dernier lieu, tri alphabétique par nom d'entreprise
      const nomA = a.entite?.nom?.toLowerCase() || '';
      const nomB = b.entite?.nom?.toLowerCase() || '';

      return nomA.localeCompare(nomB);
    });

    // Log pour debug : afficher les 10 premiers contacts triés pour le lot 102
    const lot102Contacts = sortedFiltered.filter(c => c.lot_technique?.code === '102').slice(0, 10);
    if (lot102Contacts.length > 0) {
      console.log("📋 Lot 102 - Premiers contacts triés:");
      lot102Contacts.forEach((c, idx) => {
        console.log(`${idx + 1}. ${c.entite?.nom} - Rang: ${c.rang} (type: ${typeof c.rang}) - Dept: ${c.departements?.[0]?.id || 'N/A'} - ID Contact: ${c.id}`);
      });

      // Vérifier Bureau Veritas spécifiquement - TOUS les contacts
      const bvContacts = sortedFiltered.filter(c => c.entite?.nom?.includes('Bureau Veritas Solutions'));
      if (bvContacts.length > 0) {
        console.log(`🔍 Bureau Veritas Solutions - ${bvContacts.length} contact(s) trouvé(s):`);
        bvContacts.forEach((bv, idx) => {
          console.log(`  ${idx + 1}. ID: ${bv.id} | Rang: ${bv.rang} | Lot: ${bv.lot_technique?.code} | Dept: ${bv.departements?.map(d => d.id).join(', ') || 'N/A'}`);
        });
      }
    }

    console.log("Résultat final:", sortedFiltered.length);
    console.log("=== FIN FILTRAGE ===");

    setContacts(sortedFiltered);
  };

  // Fonctions de gestion des changements de filtres
  const handleDepartementChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    const departementValue = value === "" ? "" : Number(value);
    setSelectedDepartement(departementValue);
    // Pas d'appel API, juste filtrage local
  };

  const handleLotChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    const lotValue = value === "" ? "" : Number(value);
    setSelectedLot(lotValue);
    // Pas d'appel API, juste filtrage local
  };

  const handleRangChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setSelectedRang(value);
    // Pas d'appel API, juste filtrage local
  };

  const handleNomChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSelectedNom(value);
    // Pas d'appel API, juste filtrage local
  };

  // Nouvelle fonction pour récupérer les lots techniques depuis l'API
  const fetchLotsTechniques = async () => {
    try {
      // Récupérer le token d'authentification
      const token = localStorage.getItem('token');

      // if (!token) {
      //   throw new Error("Utilisateur non authentifié");
      // }

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/lots-technique/categories`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Erreur HTTP ${response.status}`);
      }

      const result = await response.json();

      // Vérifier la structure de la réponse
      console.log("Structure des lots techniques:", result);

      // Déterminer si nous avons un tableau ou un objet avec une propriété data
      const lotsData = Array.isArray(result) ? result :
        (result.data && Array.isArray(result.data)) ? result.data :
          [];

      setLotsTechniques(lotsData);

      // ✅ Utiliser le CODE comme value (pas l'ID)
      const options: FilterOption[] = lotsData.map((lot: LotTechniqueApi) => ({
        label: `${lot.code} - ${lot.nom}`,
        value: lot.code // ✅ Utiliser le CODE qui correspond aux données des contacts
      }));

      // Trier par code numérique
      options.sort((a, b) => Number(a.value) - Number(b.value));

      // Ajouter l'option "Tous"
      options.unshift({ label: "Tous les lots", value: "" });

      setLotOptions(options);

      console.log("Options de lots créées:", options);
    } catch (error: any) {
      console.error('Erreur lors du chargement des lots techniques:', error);
      setError(`Impossible de charger les lots techniques: ${error.message}`);
    }
  };

  const compareDepartements = (a: FilterOption, b: FilterOption) => {
    // Extraire les numéros de département du label (format: "XX - Nom département")
    const numA = parseInt(a.label.split(' - ')[0]);
    const numB = parseInt(b.label.split(' - ')[0]);

    // Gérer les cas spéciaux (2A, 2B, etc.)
    if (isNaN(numA) || isNaN(numB)) {
      return a.label.localeCompare(b.label);
    }

    return numA - numB;
  };

  // Nouvelle fonction pour récupérer les départements depuis l'API
  const fetchLotsDepartements = async () => {
    try {
      // Récupérer le token d'authentification
      const token = localStorage.getItem('token');

      // if (!token) {
      //   throw new Error("Utilisateur non authentifié");
      // }

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/departements`, // URL corrigée
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Erreur HTTP ${response.status}`);
      }

      const result = await response.json();
      console.log("Structure des départements:", result);

      // Extraire les données de départements selon la structure de la réponse
      const departementsData = result.data || [];

      // Vérifier si nous avons bien un tableau de départements
      if (Array.isArray(departementsData)) {
        // Créer les options pour le dropdown des départements
        const options = departementsData.map(dep => ({
          label: `${dep.id} - ${dep.nom}`,
          value: dep.id
        }))
          .sort(compareDepartements); // Utiliser notre fonction de tri personnalisée

        setDepartementOptions(options);
      } else {
        console.error('Format de données de départements inattendu:', departementsData);
        throw new Error('Format de données de départements inattendu');
      }
    } catch (error: any) {
      console.error('Erreur lors du chargement des départements:', error);
      setError(`Impossible de charger les départements: ${error.message}`);
    }
  };

  // Extraire les rangs et noms d'entreprises à partir des données de contacts
  const extractRangAndNomOptions = (data: Contact[]) => {
    // Créer des Sets pour éviter les doublons
    const rangs = new Set<string>();
    const noms = new Set<string>();

    data.forEach(contact => {
      // Extraire les rangs
      if (contact.rang) {
        rangs.add(contact.rang.toString());
      }

      // Extraire les noms d'entreprise
      if (contact.entite?.nom) {
        noms.add(contact.entite.nom);
      }
    });

    // Définir les rangs possibles avec leurs libellés explicites
    const rangOptions = [
      { label: "Tous", value: "" },
      { label: "Titulaire de rang 1", value: "1" },
      { label: "Titulaire de rang 2", value: "2" },
      { label: "Titulaire de rang 3", value: "3" },
      { label: "Libre", value: "0" }
    ];

    const nomOptions = Array.from(noms)
      .sort()
      .map(nom => ({ label: nom, value: nom }));

    // Ajouter l'option "Tous" au début de la liste des noms
    nomOptions.unshift({ label: "Toutes les entreprises", value: "" });

    return { rangOptions, nomOptions };
  };

  useEffect(() => {
    if (allContacts.length > 0) {
      filterContactsLocally();
    }
  }, [searchQuery, selectedLot, selectedRang, selectedNom, allContacts]); // ❌ Retrait de selectedDepartement (géré côté backend)

  // Fonction pour récupérer les contacts depuis l'API
  // const fetchContacts = async (
  //   page: number = 1,
  //   perPage: number = paginationContacts.perPage,
  //   search: string = "",
  //   departement: string | number = "",
  //   lot: string | number = "",
  //   rang: string = "",
  //   nom: string = ""
  // ) => {
  //   try {
  //     setLoading(true);

  //     const queryParams = new URLSearchParams();
  //     queryParams.append('page', page.toString());
  //     queryParams.append('per_page', perPage.toString());

  //     if (search) queryParams.append('search', search);
  //     if (departement !== "") queryParams.append('departement', departement.toString());
  //     if (lot !== "") queryParams.append('lot', lot.toString());
  //     if (rang) queryParams.append('rang', rang);
  //     if (nom) queryParams.append('nom', nom);

  //     const token = localStorage.getItem('token');

  //     const response = await fetch(
  //       `${process.env.REACT_APP_API_URL}/prestataire/annuaire?${queryParams.toString()}`,
  //       {
  //         headers: {
  //           'Authorization': `Bearer ${token}`,
  //           'Content-Type': 'application/json'
  //         }
  //       }
  //     );

  //     if (!response.ok) throw new Error(`Erreur HTTP ${response.status}`);

  //     const result: ApiResponse = await response.json();
  //     console.log("Structure des contacts:", result);
  //     console.log("Last.page:", result.pagination.last_page);
  //     setContacts([...result.data]);

  //     setPaginationContacts({
  //       currentPage: result.pagination.current_page,
  //       lastPage: result.pagination.last_page,
  //       perPage: result.pagination.per_page,
  //       total: result.pagination.total
  //     });

  //     const { rangOptions: newRangOptions, nomOptions: newNomOptions } = extractRangAndNomOptions(result.data);
  //     setRangOptions(newRangOptions);
  //     setNomOptions(newNomOptions);

  //     const uniqueEntiteIds = Array.from(
  //       new Set(result.data.map(c => c.entite?.id).filter(Boolean))
  //     ) as number[];

  //     uniqueEntiteIds.forEach(id => {
  //       fetchLogoWithSessionCache(id).then(() => {
  //         // chaque logo met à jour logos au fur et à mesure via setLogos
  //       });
  //     });
  //   } catch (error: any) {
  //     console.error('Erreur lors du chargement des contacts:', error);
  //     setError(error.message);
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const fetchContacts = async () => {
    try {
      setLoading(true);

      const token = localStorage.getItem('token');

      // ✅ Construire l'URL avec le département sélectionné si présent
      const queryParams = new URLSearchParams();
      if (selectedDepartement !== "") {
        queryParams.append('departement', selectedDepartement.toString());
      }

      const url = `${process.env.REACT_APP_API_URL}/prestataire/annuaire${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      console.log("📡 Appel API avec URL:", url);

      // Charger les contacts avec le filtre département si applicable
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error(`Erreur HTTP ${response.status}`);

      const result: ApiResponse = await response.json();
      console.log("Structure des contacts:", result);

      // Dédupliquer les données par ID
      const uniqueData = Array.from(
        new Map(result.data.map(item => [item.id, item])).values()
      );

      // Stocker TOUS les contacts
      setAllContacts(uniqueData);
      setContacts(uniqueData);

      // Extraire les options de filtres
      const { rangOptions: newRangOptions, nomOptions: newNomOptions } = extractRangAndNomOptions(uniqueData);
      setRangOptions(newRangOptions);
      setNomOptions(newNomOptions);

    } catch (error: any) {
      console.error('Erreur lors du chargement des contacts:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour récupérer les données des acheteurs
  // const fetchAcheteurs = async (
  //   page: number = 1,
  //   perPage: number = paginationAcheteurs.perPage
  // ) => {
  //   try {
  //     setLoading(true);
  //     const token = localStorage.getItem('token');

  //     const queryParams = new URLSearchParams();
  //     queryParams.append('page', page.toString());
  //     queryParams.append('per_page', perPage.toString()); // <-- nouveau

  //     const response = await fetch(
  //       `${process.env.REACT_APP_API_URL}/acheteur/annuaire?${queryParams.toString()}`,
  //       {
  //         headers: {
  //           'Authorization': `Bearer ${token}`,
  //           'Content-Type': 'application/json'
  //         }
  //       }
  //     );

  //     if (!response.ok) throw new Error(`Erreur HTTP ${response.status}`);

  //     const result = await response.json();

  //     // Tri du plus récent au plus ancien selon la date de création
  //     const sortedAcheteurs = (result.data || []).sort((a: any, b: any) => {
  //       const dateA = new Date(a.created_at).getTime();
  //       const dateB = new Date(b.created_at).getTime();
  //       return dateB - dateA; // du plus récent au plus ancien
  //     });

  //     setAcheteurs(sortedAcheteurs);

  //     console.log("Structure des acheteurs:", result);
  //     console.log("Last.page acheteurs:", result.pagination.last_page);

  //     setPaginationAcheteurs({
  //       currentPage: result.pagination.current_page,
  //       lastPage: result.pagination.last_page,
  //       perPage: result.pagination.per_page,
  //       total: result.pagination.total
  //     });
  //   } catch (error: any) {
  //     console.error('Erreur lors du chargement des acheteurs:', error);
  //     setError(error.message);
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const fetchAcheteurs = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/acheteur/annuaire`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) throw new Error(`Erreur HTTP ${response.status}`);

      const result = await response.json();

      // Tri du plus récent au plus ancien selon la date de création
      const sortedAcheteurs = (result.data || []).sort((a: any, b: any) => {
        const dateA = new Date(a.created_at).getTime();
        const dateB = new Date(b.created_at).getTime();
        return dateB - dateA;
      });

      // ✅ Dédupliquer les acheteurs par ID
      const uniqueAcheteurs = Array.from(
        new Map(sortedAcheteurs.map((item: any) => [item.id, item])).values()
      );

      setAcheteurs(uniqueAcheteurs);
    } catch (error: any) {
      console.error('Erreur lors du chargement des acheteurs:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour gérer le changement d'onglet
  const handleTabChange = (index: number) => {
    setActiveTab(index);
  };

  useEffect(() => {
    fetchLotsDepartements();
    if (isAcheteurTab()) {
      fetchAcheteurs();
    } else {
      fetchLotsTechniques();
      fetchContacts(); // Recharger les contacts avec le département sélectionné
    }
  }, [activeTab, user, selectedDepartement]); // ✅ Ajout de selectedDepartement

  const getDepartementLabel = (id: number | string | undefined) => {
    if (!id) return;

    let normalizedId = String(id);

    // Retirer le 0 de tête pour la recherche
    const searchId = normalizedId.startsWith("0")
      ? normalizedId.slice(1)
      : normalizedId;

    // Trouver le département correspondant
    const dep = departementOptions.find(
      (d) => String(d.value) === searchId
    );

    // Si trouvé → reformater l’affichage avec le zéro (si besoin)
    if (dep) {
      const displayCode = normalizedId.padStart(2, "0");
      const [, nom] = dep.label.split(" - ");
      return `${displayCode} - ${nom}`;
    }

    return;
  };

  // Fonction pour gérer le changement de page
  const handlePageChange = (page: number) => {
    if (isAcheteurTab()) {
      fetchAcheteurs(); // <-- utiliser la page demandée
    } else {
    }
  };

  // Rendu des filtres dropdown
  const renderFilterDropdowns = () => (
    <div className="annuaire-filters">

      <div className="filter-dropdown">
        <label htmlFor="lot-filter">Catégorie</label>
        <select
          id="lot-filter"
          value={selectedLot}
          onChange={handleLotChange}
        >
          {lotOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      <div className="filter-dropdown">
        <label htmlFor="departement-filter">Département</label>
        <select
          id="departement-filter"
          value={selectedDepartement}
          onChange={handleDepartementChange}
        >
          {departementOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>



      <div className="filter-dropdown">
        <label htmlFor="rang-filter">Types</label>
        <select
          id="rang-filter"
          value={selectedRang}
          onChange={handleRangChange}
        >
          {rangOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      <div className="filter-dropdown">
        <label htmlFor="nom-filter">Nom entreprise</label>
        <input
          type="text"
          id="nom-filter"
          value={selectedNom}
          onChange={handleNomChange}
          placeholder="Saisir un nom d'entreprise"
        />
      </div>
    </div>
  );

  // Fonction modifiée pour définir les colonnes des acheteurs
  const getAcheteursColumns = () => {
    // Fonction utilitaire pour formater le code postal
    const formatCodePostal = (codePostal: string | undefined) => {
      if (!codePostal) return '-';

      // Codes postaux des DOM-TOM commencent par 97 ou 98
      if (codePostal.startsWith('97') || codePostal.startsWith('98')) {
        return codePostal.slice(0, 3);
      }

      // Codes postaux de la Corse commencent par 20
      if (codePostal.startsWith('20')) {
        return codePostal.slice(0, 3);
      }

      // Pour les autres départements, prendre les 2 premiers chiffres
      return codePostal.slice(0, 2);
    };

    return [
      {
        field: 'nom',
        header: 'Nom',
        renderCell: (_: any, acheteur: any) => (
          <div className="acheteur-nom-cell">
            {(acheteur.nom || acheteur.organisme?.nom || '-').toUpperCase()}
          </div>
        )
      },
      {
        field: 'localisation',
        header: 'Localisation',
        renderCell: (_: any, acheteur: any) => {
          const formatCodePostal = (codePostal: string | undefined) => {
            if (!codePostal) return '-';
            if (codePostal.startsWith('97') || codePostal.startsWith('98')) return codePostal.slice(0, 3);
            if (codePostal.startsWith('20')) return codePostal.slice(0, 3);
            return codePostal.slice(0, 2);
          };

          // Liste restreinte des pays européens avec codes ISO
          const europeanCountries: Record<string, string> = {
            'Allemagne': 'de',
            'Autriche': 'at',
            'Belgique': 'be',
            'Bulgarie': 'bg',
            'Chypre': 'cy',
            'Croatie': 'hr',
            'Danemark': 'dk',
            'Espagne': 'es',
            'Estonie': 'ee',
            'Finlande': 'fi',
            'France': 'fr',
            'Grèce': 'gr',
            'Hongrie': 'hu',
            'Irlande': 'ie',
            'Italie': 'it',
            'Lettonie': 'lv',
            'Lituanie': 'lt',
            'Luxembourg': 'lu',
            'Malte': 'mt',
            'Pays-Bas': 'nl',
            'Pologne': 'pl',
            'Portugal': 'pt',
            'République tchèque': 'cz',
            'Roumanie': 'ro',
            'Slovaquie': 'sk',
            'Slovénie': 'si',
            'Suède': 'se',
            'Monaco': 'mc',
          };

          const countryName = acheteur.adresse?.pays?.trim() || '';
          const countryCode = europeanCountries[countryName] || null;

          return (
            <div className="acheteur-localisation-cell" style={{ display: 'flex', alignItems: 'center' }}>
              <span className="code-postal">
                {getDepartementLabel(acheteur.adresse?.departement) ||
                  `${formatCodePostal(acheteur?.adresse?.code_postal)} - ${acheteur?.adresse?.departement}`}
              </span>

              <FlagCell country={countryName} code={countryCode || undefined} />
            </div>
          );
        },
      }
    ];
  };

  // Rendu des acheteurs
  const renderAcheteurs = () => {
    if (loading) {
      return <div className="loading">Chargement des acheteurs...</div>;
    }

    if (error) {
      return <div className="error">{error}</div>;
    }

    return (
      // <TableauDeBord
      //   data={acheteurs}
      //   columns={getAcheteursColumns()}
      //   searchPlaceholder="Rechercher un acheteur"
      //   typeFilters={false}
      //   onSearch={handleSearch}
      //   onExport={handleExport}
      //   onFilter={handleFilter}
      //   isSelectable={true}
      //   // onRowClick={() => { }}
      //   showActionsColumn={false}
      //   currentPage={paginationAcheteurs.currentPage}
      //   totalPages={paginationAcheteurs.lastPage}
      //   totalItems={paginationAcheteurs.total}
      //   onPageChange={handlePageChange}
      //   showTotalCount={false}
      // />
      <TableauDeBord
        data={acheteurs}
        columns={getAcheteursColumns()}
        searchPlaceholder="Rechercher un acheteur"
        typeFilters={false}
        onSearch={handleSearch}
        onExport={handleExport}
        onFilter={handleFilter}
        isSelectable={true}
        showActionsColumn={false}
        showPagination={false}
      />
    );
  };

  return (
    <div className="annuaire-page">
      <h1 className="page-title">Annuaire</h1>
      <div className="annuaire-content">
        <TabNav
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={handleTabChange}
        />

        {isAcheteurTab() ? (
          // Afficher la liste des acheteurs
          renderAcheteurs()
        ) : (
          <>
            {/* Afficher les filtres et le tableau pour l'annuaire des prestataires */}
            {renderFilterDropdowns()}
            {loading ? (
              <div className="loading">Chargement des contacts...</div>
            ) : error ? (
              <div className="error">{error}</div>
            ) : (
              // <TableauDeBord
              //   data={contacts}
              //   columns={getColumns()}
              //   searchPlaceholder="Trouver un Contact"
              //   typeFilters={false}
              //   onSearch={handleSearch}
              //   onExport={handleExport}
              //   onFilter={handleFilter}
              //   isSelectable={true}
              //   // onRowClick={handleRowClick}
              //   showActionsColumn={false}
              //   currentPage={paginationContacts.currentPage}
              //   totalPages={paginationContacts.lastPage}
              //   totalItems={paginationContacts.total}
              //   onPageChange={handlePageChange}
              // />

              <TableauDeBord
                data={contacts}
                columns={getColumns()}
                searchPlaceholder="Trouver un Contact"
                typeFilters={false}
                onSearch={handleSearch}
                onExport={handleExport}
                onFilter={handleFilter}
                isSelectable={true}
                // onRowClick={handleRowClick}
                showActionsColumn={false}
                showPagination={false}
              />
            )}

            {openDocumentModalId !== null && (
              <div
                className="modal-overlay"
                onClick={() => setOpenDocumentModalId(null)}
              >
                <div
                  className="modal-content"
                  onClick={(e) => e.stopPropagation()}
                >
                  <h3>{modalTitle}</h3>
                  <DocumentsList documents={modalDocuments || []} variant="fullWidth" />

                  {/* Bouton Fermer */}
                  <Button value="Fermer" size='small' onClick={() => setOpenDocumentModalId(null)} />
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Annuaire;